// src/scripts/sqlApiHelper/utilitiesApi.js

import { db } from "@/firebase";
import {
  doc,
  collection,
  getDocs,
  query,
  where,
  orderBy,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";

import {
  toISODate,
  addDaysISO,
  parseISODate,
} from "@/scripts/sharedHelpers/formatter";

const SEND_UTILITIES_EMAIL_FUNCTION = "sendUtilitiesSubmissionEmail";

function cleanString(value) {
  return String(value || "").trim();
}

/**
 * Today as YYYY-MM-DD using formatter.js.
 */
function todayISO() {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  return toISODate(d);
}

/**
 * Monday–Sunday bounds for an anchor ISO date.
 */
function weekBoundsISO(anchorISO) {
  const dt = parseISODate(anchorISO);

  if (!dt) {
    return {
      start: "",
      end: "",
    };
  }

  const dow = dt.getDay(); // 0 Sun .. 6 Sat
  const offsetToMon = (dow + 6) % 7;

  const start = addDaysISO(anchorISO, -offsetToMon);
  const end = addDaysISO(start, 6);

  return {
    start,
    end,
  };
}

/**
 * Only treat values as provided if:
 * - finite number
 * - greater than 0
 *
 * This prevents zero/null/empty overwrites.
 */
function providedNumber(v) {
  if (v === null || v === undefined || v === "") {
    return null;
  }

  const n = Number(v);

  if (!Number.isFinite(n)) {
    return null;
  }

  if (n <= 0) {
    return null;
  }

  return n;
}

/**
 * Build Firestore update object from submitted values.
 */
function buildUtilityUpdates(src = {}) {
  const updates = {};

  const gas_m3 = providedNumber(src.gas_m3);
  if (gas_m3 !== null) {
    updates.gas_m3 = gas_m3;
  }

  const water = providedNumber(src.water);
  if (water !== null) {
    updates.water = water;
  }

  const electric_kwh_morning = providedNumber(src.electric_kwh_morning);
  if (electric_kwh_morning !== null) {
    updates.electric_kwh_morning = electric_kwh_morning;
  }

  const electric_kwh_afternoon = providedNumber(src.electric_kwh_afternoon);
  if (electric_kwh_afternoon !== null) {
    updates.electric_kwh_afternoon = electric_kwh_afternoon;
  }

  const electric_kwh_evening = providedNumber(src.electric_kwh_evening);
  if (electric_kwh_evening !== null) {
    updates.electric_kwh_evening = electric_kwh_evening;
  }

  return updates;
}

/**
 * Sends the utility submission email via Firebase Callable Function.
 *
 * Email failure should NOT undo the Firestore save.
 */
async function sendUtilitiesSubmissionEmail({
  venue_id,
  venue_name,
  postcode,
  trading_date,
  payload,
}) {
  const functions = getFunctions();
  const callable = httpsCallable(functions, SEND_UTILITIES_EMAIL_FUNCTION);

  const result = await callable({
    venue_id,
    venue_name,
    postcode,
    trading_date,
    payload,
  });

  return result?.data || {
    ok: false,
    error: "No email response returned.",
  };
}

/**
 * Firestore: Upsert today's readings using merge=true.
 *
 * Accepts either:
 * - utilitiesDayInsert({ venue_id, gas_m3, water, ... })
 * - utilitiesDayInsert({ venue_id, payload: { gas_m3, water, ... } })
 *
 * Return shape:
 * {
 *   ok: true,
 *   changed: true/false,
 *   trading_date,
 *   emailSent: true/false,
 *   emailError: string|null
 * }
 */
export async function utilitiesDayInsert(args = {}) {
  const venue_id = cleanString(args.venue_id);

  if (!venue_id) {
    return {
      ok: false,
      changed: false,
      error: "Missing venue_id",
      emailSent: false,
      emailError: null,
    };
  }

  const venue_name = cleanString(args.venue_name || args.venueName);
  const postcode = cleanString(
    args.postcode ||
      args.Postcode ||
      args.post_code ||
      args.postCode
  );

  const src =
    args.payload && typeof args.payload === "object"
      ? args.payload
      : args;

  const trading_date = todayISO();

  const updates = buildUtilityUpdates(src);

  if (Object.keys(updates).length === 0) {
    console.log("[UTILITIES] No values provided. Nothing written.", {
      venue_id,
      trading_date,
      input: src,
    });

    return {
      ok: true,
      changed: false,
      trading_date,
      message: "No values provided.",
      emailSent: false,
      emailError: null,
    };
  }

  const ref = doc(
    db,
    "venues",
    venue_id,
    "utilities_day_readings",
    trading_date
  );

  const docData = {
    trading_date,
    ...updates,
    updated_at: serverTimestamp(),
  };

  console.log("[UTILITIES] Writing to Firestore", {
    path: `venues/${venue_id}/utilities_day_readings/${trading_date}`,
    data: {
      ...docData,
      updated_at: "(serverTimestamp)",
    },
  });

  await setDoc(ref, docData, { merge: true });

  console.log("[UTILITIES] Write complete", {
    path: `venues/${venue_id}/utilities_day_readings/${trading_date}`,
    writtenFields: Object.keys(updates),
  });

  let emailSent = false;
  let emailError = null;
  let emailResult = null;

  try {
    emailResult = await sendUtilitiesSubmissionEmail({
      venue_id,
      venue_name,
      postcode,
      trading_date,
      payload: updates,
    });

    if (!emailResult?.ok) {
      throw new Error(emailResult?.error || "Utility email failed.");
    }

    emailSent = true;
  } catch (e) {
    emailSent = false;
    emailError = e?.message || String(e);

    console.error("[UTILITIES] Email failed after Firestore save", {
      venue_id,
      trading_date,
      emailError,
    });
  }

  return {
    ok: true,
    changed: true,
    inserted: null,
    trading_date,
    writtenFields: Object.keys(updates),
    emailSent,
    emailError,
    emailResult,
  };
}

/**
 * Firestore: Get current week Monday–Sunday readings for a venue.
 */
export async function utilitiesWeekGet({ venue_id } = {}) {
  venue_id = cleanString(venue_id);

  if (!venue_id) {
    return {
      ok: false,
      error: "Missing venue_id",
      start: "",
      end: "",
      rows: [],
    };
  }

  const anchor = todayISO();
  const { start, end } = weekBoundsISO(anchor);

  if (!start || !end) {
    return {
      ok: false,
      error: "Failed to calculate week bounds",
      start: "",
      end: "",
      rows: [],
    };
  }

  const colRef = collection(
    db,
    "venues",
    venue_id,
    "utilities_day_readings"
  );

  const qy = query(
    colRef,
    where("trading_date", ">=", start),
    where("trading_date", "<=", end),
    orderBy("trading_date", "asc")
  );

  const snap = await getDocs(qy);

  const rows = snap.docs.map((d) => {
    const v = d.data() || {};

    return {
      id: d.id,
      trading_date: v.trading_date || d.id,
      gas_m3: v.gas_m3 ?? null,
      electric_kwh_morning: v.electric_kwh_morning ?? null,
      electric_kwh_afternoon: v.electric_kwh_afternoon ?? null,
      electric_kwh_evening: v.electric_kwh_evening ?? null,
      water: v.water ?? null,
    };
  });

  return {
    ok: true,
    start,
    end,
    rows,
  };
}